/**
 * The client credentials the LiveSocket authenticates with, and a record of
 * which pair the socket actually connected with.
 *
 * Why this exists as shared state rather than a local in app.js: two things
 * need it. app.js reads it from the LiveSocket `params` closure, and the
 * ExtensionBridge hook writes it when the extension port hands over a fresh
 * pair. Before, the hook had no way to update the credentials app.js would
 * send, so its "reconnect with the new credentials" path would have resent the
 * old ones.
 *
 * The rules that make this work:
 *
 *   - `BrainboxLive` authenticates purely from connect params — see
 *     `fetch_credentials/2`, whose session argument is ignored. There is no
 *     server-side session to fall back on.
 *   - LiveSocket re-evaluates its `params` option on every socket connect, so
 *     whatever is set here at connect time is what the server validates.
 *   - `params` is also re-read on every channel join and rejoin, and
 *     `get_connect_params/1` reads the JOIN payload — so a LiveView auto-rejoin
 *     after a channel error picks up whatever is set here with no socket cycle.
 *   - Consequently a change here only reaches the server on the next connect or
 *     rejoin. Forcing that is the only reason to ever cycle the socket.
 */

// `import_session` is the browser-data import session the extension hands over
// alongside the credentials during the AI onboarding, or null. It rides the
// same connect params; the server verifies it and otherwise ignores it.
// `wavebox_version` / `wavebox_channel` describe the install the panel runs
// in (e.g. "153.2.196" / "stable"). They ride the same connect params so the
// release tools can tell the user whether they are up to date; the server
// ignores them for authentication.
// `ui_language` is the BCP 47 tag the extension UI is rendered in ("en-GB").
// An onboarding recording keeps it with the version and channel so the
// workbench can name the client's localised Spaces; the server validates it
// and it plays no part in authentication.
// `wizard_answers` is what the Wavebox first-run wizard learned about the user
// before the getting-started chat opened, as a JSON string, or null. The server
// parses it defensively and stamps it on that chat; it plays no part in
// authentication either.
// `onboarding_review` carries bounded step evidence separately from model
// answers. It is informational, and the server validates its schema and scope.
// `bridge_features` is what the extension says it can do over the port
// (`['origin', 'entityIcon', 'chatIndex', 'focusOrigin', 'openChat']`), from
// its `connected` message, or null from a client that predates the list. The
// page reads it through `hasFeature`; it rides the connect params so the
// server knows too, and plays no part in authentication.
let credentials = {
  client_id: null,
  client_token: null,
  import_session: null,
  wavebox_version: null,
  wavebox_channel: null,
  ui_language: null,
  wizard_answers: null,
  onboarding_review: null,
  bridge_features: null
}

// The credentials the most recent connect/join actually carried. Null until the
// LiveSocket has evaluated its `params` option at least once.
let sentCredentials = null

// Set when the credentials came from URL params, which exist to authenticate as
// a chosen user for dev/test. The extension must not quietly swap that for
// whoever is signed into Wavebox, so a pinned pair wins over anything the
// extension port later offers.
let pinned = false

/**
 * Return a copy of the credentials and context to send on the next socket join.
 */
export function getCredentials () {
  return { ...credentials }
}

/**
 * Replace the credentials and connection context, clearing absent values to null.
 * Only string wizard answers and review evidence reach the server parser.
 * @param {object | null | undefined} authData - The extension or URL authentication data.
 */
export function setCredentials (authData) {
  credentials = {
    client_id: authData?.client_id || null,
    client_token: authData?.client_token || null,
    import_session: authData?.import_session || null,
    wavebox_version: authData?.wavebox_version || null,
    wavebox_channel: authData?.wavebox_channel || null,
    ui_language: typeof authData?.ui_language === 'string' ? authData.ui_language : null,
    wizard_answers: typeof authData?.wizard_answers === 'string' ? authData.wizard_answers : null,
    onboarding_review: typeof authData?.onboarding_review === 'string' ? authData.onboarding_review : null,
    bridge_features: bridgeFeatures(authData?.bridge_features)
  }
}

/**
 * The extension's feature list as an array of strings, or null when it sent
 * none (every client before the list) or something that is not a list.
 * @param {*} features - `bridge_features` from the `connected` message.
 * @returns {string[] | null}
 */
function bridgeFeatures (features) {
  if (!Array.isArray(features)) { return null }
  return features.filter((feature) => typeof feature === 'string')
}

/**
 * Record the feature list from a `connected` message without touching the
 * credentials (the pinned dev/test pair keeps its identity, but the page still
 * learns what the extension it is talking to can do). The server sees a change
 * on the next join.
 * @param {*} features - `bridge_features` from the `connected` message.
 */
export function setBridgeFeatures (features) {
  credentials = { ...credentials, bridge_features: bridgeFeatures(features) }
}

/**
 * Whether the extension on the other end of the port said it can do `name`.
 * False for every client that predates `bridge_features`.
 * @param {string} name - A feature name, e.g. `entityIcon`.
 * @returns {boolean}
 */
export function hasFeature (name) {
  return Array.isArray(credentials.bridge_features) && credentials.bridge_features.includes(name)
}

/**
 * Seed from the dev/test URL params. Pins if both values are present, so the
 * extension cannot override an explicitly requested identity.
 */
export function pinCredentials (authData) {
  setCredentials(authData)
  pinned = hasCredentials()
}

/**
 * Report whether both credential fields are present, without validating them.
 */
export function hasCredentials () {
  return !!(credentials.client_id && credentials.client_token)
}

/**
 * Called from the LiveSocket `params` closure. Records what is about to go on
 * the wire so `socketNeedsReconnect` can tell a genuine credential change from
 * a repeat of the pair the connection already holds.
 */
export function recordCredentialsSent () {
  sentCredentials = { ...credentials }
}

/**
 * Whether the socket must be cycled for the server to see `authData`.
 *
 * False for the normal launch, where app.js has already awaited the extension
 * credentials and connected with them — reconnecting there buys nothing and
 * costs a visible disconnect, a duplicate mount, and a duplicate Wavebox API
 * validation. True only when the extension hands over a pair the current
 * connection is not carrying, which is what a token refresh or a switch of
 * signed-in user looks like from here.
 */
export function socketNeedsReconnect (authData) {
  if (pinned) { return false }
  if (!authData?.client_id || !authData?.client_token) { return false }
  if (!sentCredentials) { return true }

  return (
    sentCredentials.client_id !== authData.client_id ||
    sentCredentials.client_token !== authData.client_token
  )
}
